import React, { useState, useRef, useEffect } from "react";
import { Plus, ChevronDown } from "lucide-react";
import toast from "react-hot-toast";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

export const ReportsList = ({
  reports,
  onCreateClick,
  onReportSelect,
  onDeleteReport,
}) => {
  const navigate = useNavigate();
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRefs = useRef({});

  useEffect(() => {
    if (!openDropdownId) return;
    const handleClickOutside = (e) => {
      if (!buttonRefs.current[openDropdownId]?.contains(e.target) && 
          !e.target.closest(".reports-dropdown-menu")) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDropdownId]);

  useEffect(() => {
    if (openDropdownId && buttonRefs.current[openDropdownId]) {
      const rect = buttonRefs.current[openDropdownId].getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: Math.min(rect.left + window.scrollX, window.innerWidth - 160),
      });
    }
  }, [openDropdownId]);

  const handleDelete = (report) => {
    toast((t) => (
      <div className="flex flex-col gap-3">
        <p>Delete report for {report.teamName}?</p>
        <div className="flex gap-2">
          <button
            className="px-3 py-1.5 text-sm text-white bg-red-500 rounded-lg hover:bg-red-600"
            onClick={() => { onDeleteReport(report.id); toast.dismiss(t.id); }}
          >
            Delete
          </button>
          <button
            className="px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
            onClick={() => toast.dismiss(t.id)}
          >
            Cancel
          </button>
        </div>
      </div>
    ), { duration: 5000 });
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const btnClass = "px-3 py-1.5 text-xs font-medium bg-white border border-blue-200 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors whitespace-nowrap";

  const ReportCard = ({ report }) => (
    <div className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 hover:shadow-sm transition-all">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-semibold text-gray-900 truncate">{report.teamName}</h3>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-sm text-gray-500">
            <span className="font-medium text-gray-700">{report.year}</span>
            <span>·</span>
            <span>{report.numPitchers} pitchers, {report.numHitters} hitters</span>
            {formatDate(report.dateCreated) && (
              <>
                <span className="hidden sm:inline">·</span>
                <span className="hidden sm:inline text-gray-400">Created {formatDate(report.dateCreated)}</span>
              </>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => onReportSelect(report)}
            className={btnClass}
          >
            View/Edit
          </button>
          
          <div className="relative">
            <button
              ref={(el) => { if (el) buttonRefs.current[report.id] = el; }}
              onClick={() => setOpenDropdownId(openDropdownId === report.id ? null : report.id)}
              className={`${btnClass} inline-flex items-center gap-1`}
            >
              Reports
              <ChevronDown size={14} className={`transition-transform ${openDropdownId === report.id ? 'rotate-180' : ''}`} />
            </button>
            {openDropdownId === report.id && createPortal(
              <div
                className="reports-dropdown-menu fixed bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
                style={{ top: dropdownPosition.top, left: dropdownPosition.left, minWidth: 150 }}
              >
                <button
                  onClick={() => {
                    setOpenDropdownId(null);
                    navigate(`/scouting/reports/${report.id}/spraycharts`);
                  }}
                  className="w-full px-3 py-2 text-sm text-left text-gray-700 hover:bg-blue-50"
                >
                  Spray Charts
                </button>
                <button
                  onClick={() => {
                    setOpenDropdownId(null);
                    navigate(`/scouting/reports/${report.id}/baserunning`);
                  }}
                  className="w-full px-3 py-2 text-sm text-left text-gray-700 hover:bg-blue-50"
                >
                  Baserunning
                </button>
              </div>,
              document.body
            )}
          </div>
          
          <button
            onClick={() => handleDelete(report)}
            className={btnClass}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200">
      <div className="flex justify-between items-center mb-4">
        <button
          onClick={onCreateClick}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors font-medium"
        >
          <Plus size={16} />
          New Report
        </button>
      </div>

      {reports.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-gray-200 rounded-lg">
          <p className="text-gray-500">No reports created yet</p>
          <p className="text-gray-400 mt-1 text-sm">Click &ldquo;New Report&rdquo; to get started</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map((report) => (
            <ReportCard key={report.id} report={report} />
          ))}
        </div>
      )}
    </div>
  );
};
